import { css } from "@emotion/css";
import MuiCircularProgress from "@mui/material/CircularProgress";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
export interface SharedCircularProgressProps {
    /**
     * The size of the component.
     * Only two sizes are currently supported, small and large.
     */
    size: CircularProgressSize;
}
export interface IndeterminateCircularProgressProps extends SharedCircularProgressProps {
    /**
     * Whether to show the circular progress as determinate.
     *
     * The progress should be shown as indeterminate when an action's overall completion can not be determined
     * such as loading data in the background.
     */
    determinate?: false;
}
export interface DeterminateCircularProgressProps extends SharedCircularProgressProps {
    /**
     * The completion value in percent expressed as a numeric value between 0-100.
     */
    value: number;
    /**
     * Whether to show the circular progress as determinate.
     *
     * The progress should be shown as determinate, that is, it is known how long an
     * action will take.
     */
    determinate: true;
}
/** Props used for the {@link CircularProgress} component */
type CircularProgressProps = IndeterminateCircularProgressProps | DeterminateCircularProgressProps;
/**
 * - Docs: https://www.octopus.design/latest/components/status-indicators/progress/overview-IGDi9OpK
 * - Showcase: https://components.octopus.design/?story=progress--circular
 * @example
 * ```tsx
 * <CircularProgressProps size="small" />
 * ```
 * Or for determinate progress:
 * <CircularProgress size="small" determinate >
 */
export const CircularProgress: React.FC<CircularProgressProps> = (props) => {
    const { size } = props;
    if (isDeterminateCircularProgress(props)) {
        return <MuiCircularProgress classes={{ root: rootStyles }} role={"progressbar"} aria-label={"Progress"} size={getProgressSize(size)} variant={"determinate"} value={props.value} thickness={6}/>;
    }
    return <MuiCircularProgress classes={{ root: rootStyles }} role="progressbar" aria-label="Progress" size={getProgressSize(size)} variant="indeterminate" thickness={6}/>;
};
CircularProgress.displayName = "CircularProgress"
function isDeterminateCircularProgress(props: CircularProgressProps): props is DeterminateCircularProgressProps {
    return !!props.determinate;
}
type CircularProgressSize = "small" | "large" | "container";
const circularProgressClasses = {
    root: "MuiCircularProgress-root",
};
const rootStyles = css({
    [`&.${circularProgressClasses.root}`]: {
        color: themeTokens.color.progressBar.foreground.circular,
        // If the circular progress is used in a flex container, the explicit size may by ignored due to the default flex-shrink value of 1.
        // We can disable this by setting flex-shrink to 0, so that this component is reliably sized in a flex container.
        flexShrink: 0,
    },
});
function getProgressSize(size: CircularProgressSize): string {
    switch (size) {
        case "small":
            return "1.375rem";
        case "large":
            return "2.75rem";
        case "container":
            return "100%";
    }
}
CircularProgress.displayName = "CircularProgress";
